import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CapabilitiesProvider } from "@/context/CapabilitiesContext";
import { RolesProvider } from "@/context/RolesContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { DialogProvider } from "@/context/DialogContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Attendance from "@/pages/Attendance";
import Leaves from "@/pages/Leaves";
import Projects from "@/pages/Projects";
import ProjectKanban from "@/pages/ProjectKanban";
import ProjectReport from "@/pages/ProjectReport";
import Directory from "@/pages/Directory";
import Profile from "@/pages/Profile";
import Assets from "@/pages/Assets";
import Announcements from "@/pages/Announcements";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Timesheet from "@/pages/Timesheet";
import Expenses from "@/pages/Expenses";
import Onboarding from "@/pages/Onboarding";
import Offboarding from "@/pages/Offboarding";
import AllTasks from "@/pages/AllTasks";
import Gamification from "@/pages/Gamification";
import ProjectBudgetPage from "@/pages/ProjectBudgetPage";
import Insights from "@/pages/Insights";
import Referrals from "@/pages/Referrals";
import Jobs from "@/pages/Jobs";
import JobEditor from "@/pages/JobEditor";
import JobApplicants from "@/pages/JobApplicants";
import ApplicationDetail from "@/pages/ApplicationDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Issues from "@/pages/Issues";
import IssueDetail from "@/pages/IssueDetail";
import Sprints from "@/pages/Sprints";
import SprintReport from "@/pages/SprintReport";
import Backlog from "@/pages/Backlog";
import IdleTrackerPage from "@/pages/IdleTracker";
import PerformanceReviewsList, { PerformanceReviewDetail } from "@/pages/PerformanceReviews";
import ProjectFiles from "@/pages/ProjectFiles";
import LeaveCalendar from "@/pages/LeaveCalendar";
import { Toaster } from "sonner";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <AuthProvider>
            <CapabilitiesProvider>
              <RolesProvider>
                <DialogProvider>
                  <Toaster position="top-right" />
                  <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/leaves" element={<Leaves />} />
                <Route path="/leave-calendar" element={<LeaveCalendar />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectKanban />} />
                <Route path="/projects/:id/sprints" element={<Sprints />} />
                <Route path="/projects/:pid/sprints/:sid/report" element={<SprintReport />} />
                <Route path="/projects/:id/backlog" element={<Backlog />} />
                <Route path="/projects/:id/report" element={<ProtectedRoute roles={["admin", "manager", "hr"]}><ProjectReport /></ProtectedRoute>} />
                <Route path="/projects/:id/budget" element={<ProtectedRoute caps={["view_project_budget"]}><ProjectBudgetPage /></ProtectedRoute>} />
                <Route path="/projects/:id/files" element={<ProjectFiles />} />
                <Route path="/tasks" element={<AllTasks />} />
                <Route path="/gamification" element={<ProtectedRoute caps={["use_gamification"]}><Gamification /></ProtectedRoute>} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/assets" element={<Assets />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/reports" element={<ProtectedRoute roles={["admin", "manager", "hr"]}><Reports /></ProtectedRoute>} />
                <Route path="/directory" element={<ProtectedRoute roles={["admin", "manager", "hr"]}><Directory /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute caps={["manage_permissions", "manage_roles", "manage_timesheet_config", "manage_branding", "manage_email", "manage_expenses_config", "manage_onboarding_config", "create_leave_templates", "manage_referrals_config", "manage_job_templates", "manage_issue_templates", "manage_backups", "manage_idle_tracker_config", "manage_holidays"]}><Settings /></ProtectedRoute>} />
                <Route path="/timesheet" element={<ProtectedRoute caps={["use_timesheet", "approve_timesheets"]}><Timesheet /></ProtectedRoute>} />
                <Route path="/expenses" element={<ProtectedRoute caps={["use_expenses", "approve_expenses", "view_all_expenses"]}><Expenses /></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute caps={["use_onboarding"]}><Onboarding /></ProtectedRoute>} />
                <Route path="/offboarding" element={<ProtectedRoute caps={["use_offboarding"]}><Offboarding /></ProtectedRoute>} />
                <Route path="/referrals" element={<ProtectedRoute caps={["use_referrals"]}><Referrals /></ProtectedRoute>} />
                <Route path="/jobs" element={<ProtectedRoute caps={["use_jobs"]}><Jobs /></ProtectedRoute>} />
                <Route path="/jobs/new" element={<ProtectedRoute caps={["manage_jobs"]}><JobEditor /></ProtectedRoute>} />
                <Route path="/jobs/:id" element={<ProtectedRoute caps={["use_jobs"]}><JobEditor /></ProtectedRoute>} />
                <Route path="/jobs/:jid/applicants" element={<ProtectedRoute caps={["use_jobs"]}><JobApplicants /></ProtectedRoute>} />
                <Route path="/applications/:aid" element={<ProtectedRoute caps={["use_jobs"]}><ApplicationDetail /></ProtectedRoute>} />
                <Route path="/issues" element={<ProtectedRoute caps={["use_issues"]}><Issues /></ProtectedRoute>} />
                <Route path="/issues/:id" element={<ProtectedRoute caps={["use_issues"]}><IssueDetail /></ProtectedRoute>} />
                <Route path="/idle-tracker" element={<ProtectedRoute caps={["view_idle_tracker"]}><IdleTrackerPage /></ProtectedRoute>} />
                <Route path="/performance" element={<PerformanceReviewsList />} />
                <Route path="/performance/:rid" element={<PerformanceReviewDetail />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
                </DialogProvider>
              </RolesProvider>
            </CapabilitiesProvider>
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
    </div>
  );
}
export default App;
